<template>
  <v-app class="staff-layout">
    <header class="app-header">
      <div class="header-left">
        <button type="button" class="app-brand app-brand-btn" @click="goMember">
          <span class="app-title">CAION</span>
          <span class="app-tagline">장기요양 통합관리</span>
        </button>
        <nav class="staff-top-nav" aria-label="메뉴">
          <router-link
            v-for="item in navItems"
            :key="item.to"
            :to="item.to"
            class="staff-nav-link"
            :class="{ active: isActive(item.to) }"
          >
            {{ item.title }}
          </router-link>
        </nav>
      </div>

      <div class="header-right">
        <div v-if="isImpersonating" class="impersonate-indicator" @click="exitImpersonation">
          <v-icon size="18" class="impersonate-icon">mdi-incognito</v-icon>
          <div class="impersonate-info">
            <span class="from-user">{{ originalUser?.name }}</span>
            <span class="arrow">→</span>
            <span class="to-user">{{ currentUser?.name }}</span>
          </div>
          <v-icon size="16" class="exit-icon">mdi-close-circle</v-icon>
        </div>

        <div class="user-info" v-if="!isImpersonating && currentUser">
          <span class="header-role-badge">{{ currentUser.roleName }}</span>
          <span class="user-name">{{ currentUser.name }}님</span>
        </div>

        <v-menu>
          <template v-slot:activator="{ props }">
            <v-btn icon size="small" variant="text" color="white" v-bind="props">
              <v-icon>mdi-account-circle</v-icon>
            </v-btn>
          </template>
          <v-list density="compact">
            <template v-if="isImpersonating">
              <v-list-item class="impersonate-menu-item">
                <div class="impersonate-menu-info">
                  <v-icon size="small" color="deep-purple">mdi-incognito</v-icon>
                  <div class="info-text">
                    <span class="label">접속 중인 계정</span>
                    <span class="value">{{ currentUser?.name }}</span>
                  </div>
                </div>
              </v-list-item>
              <v-list-item @click="exitImpersonation" class="exit-menu-item">
                <template v-slot:prepend>
                  <v-icon size="small" color="error">mdi-arrow-left-circle</v-icon>
                </template>
                <v-list-item-title class="text-error">{{ originalUser?.name }}(으)로 복귀</v-list-item-title>
              </v-list-item>
              <v-divider />
            </template>
            <v-list-item v-if="!isImpersonating" @click="profileDialog = true">
              <template v-slot:prepend>
                <v-icon size="small">mdi-account-cog-outline</v-icon>
              </template>
              <v-list-item-title>내 정보</v-list-item-title>
            </v-list-item>
            <v-divider v-if="!isImpersonating" />
            <v-list-item @click="handleLogout">
              <template v-slot:prepend>
                <v-icon size="small">mdi-logout</v-icon>
              </template>
              <v-list-item-title>로그아웃</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </header>

    <div class="staff-main">
      <slot />
    </div>

    <UserProfileDialog v-model="profileDialog" />

    <v-dialog v-model="exitDialog" max-width="380px">
      <v-card class="exit-dialog">
        <v-card-title class="exit-header">
          <span class="exit-header-title">
            <v-icon class="mr-2" color="warning">mdi-arrow-left-circle</v-icon>
            권한 복귀
          </span>
          <button type="button" class="exit-close" aria-label="닫기" @click="exitDialog = false">
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </v-card-title>
        <v-card-text class="exit-body">
          <p><strong>{{ originalUser?.name }}</strong> 계정으로 돌아가시겠습니까?</p>
        </v-card-text>
        <v-card-actions class="exit-actions">
          <v-spacer />
          <v-btn variant="text" class="btn-cancel" @click="exitDialog = false">취소</v-btn>
          <v-btn variant="flat" class="btn-confirm" @click="confirmExit">복귀하기</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import UserProfileDialog from '@/components/common/UserProfileDialog.vue';

const store = useStore();
const router = useRouter();
const route = useRoute();

const exitDialog = ref(false);
const profileDialog = ref(false);

const navItems = [
  { title: '고객관리', to: '/member' },
  { title: '시설검색', to: '/facility' },
];

const currentUser = computed(() => store.getters.currentUser);
const isImpersonating = computed(() => store.getters.isImpersonating);
const originalUser = computed(() => store.getters.originalUser);

const isActive = (to: string) => {
  if (to === '/member') {
    return route.path === '/' || route.path === '/member' || route.path.startsWith('/member/');
  }
  return route.path === to || route.path.startsWith(`${to}/`);
};

const goMember = () => {
  router.push('/member');
};

const handleLogout = () => {
  store.dispatch('logout');
  // 진행 중 API 실패 스낵바가 덮어쓰지 않도록 로그인으로 즉시 이동
  window.location.href = '/login';
};

const exitImpersonation = () => {
  exitDialog.value = true;
};

const confirmExit = async () => {
  await store.dispatch('exitImpersonation');
  window.location.reload();
};
</script>
